import { useState } from "react";
import { useNavigate } from "react-router-dom";
export interface TrialProduct {
  id: string;
  name: string;
  description: string;
  price: number;
  sellingPrice: number;
  returnRate: number;
  raised: number;
  target: number;
  stockQuantity: number;
  imageUrl?: string;
  tag: string;
  status: string;
}
import AuthModal from "./AuthModal";
import { useAuth } from "../../../hooks/useAuth";

interface ProductCardProps {
  product: TrialProduct;
  onOrder: (productId: string) => void;
}

function ProductCard({ product, onOrder }: ProductCardProps) {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [imgError, setImgError] = useState(false);
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const profit = product.sellingPrice - product.price;
  const remaining = Math.max(0, product.stockQuantity - product.raised);

  const progress =
    product.stockQuantity > 0
      ? Math.min(
          100,
          Math.round((product.raised / product.stockQuantity) * 100),
        )
      : 0;

  const progressColor =
    progress >= 80
      ? "bg-emerald-500"
      : progress >= 50
        ? "bg-amber-500"
        : "bg-rose-500";

  const handleOrder = () => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    navigate(`/product/${product.id}`);
  };

  return (
    <>
      <div className="group relative bg-white rounded-2xl border border-stone-200 overflow-hidden hover:border-rose-300 transition-all duration-300 hover:shadow-lg">
        {/* Clickable card area */}
        <button
          onClick={() => navigate(`/product/${product.id}`)}
          className="w-full text-left block"
        >
          {/* Image area */}
          <div className="relative w-full h-48 md:h-56 bg-stone-100 flex items-center justify-center overflow-hidden">
            {product.imageUrl && !imgError ? (
              <img
                src={product.imageUrl}
                alt={product.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                onError={() => setImgError(true)}
              />
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center">
                  <i className="ri-sparkling-line text-rose-600 text-xl" />
                </div>
                <span className="font-body text-xs text-stone-400">{product.name}</span>
              </div>
            )}

            {/* Tag */}
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-800 text-white font-body text-[10px] font-semibold">
                <i className="ri-sparkling-line" />
                {product.tag}
              </span>
            </div>

            {/* Remaining badge */}
            <div className="absolute top-3 right-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm text-stone-700 font-body text-[10px] font-semibold border border-stone-100">
                <i className="ri-coins-line text-amber-500" />
                剩 {remaining} 件
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-5">
            <h3 className="font-display text-base md:text-lg font-semibold text-stone-800 mb-1 line-clamp-1">
              {product.name}
            </h3>
            <p className="font-body text-xs text-stone-500 mb-3 line-clamp-2 leading-relaxed">
              {product.description}
            </p>

            {/* Key stats row */}
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-stone-100">
              <div className="flex-1">
                <p className="font-body text-[10px] text-stone-400">認購價</p>
                <p className="font-display text-sm font-bold text-stone-800">
                  NT$ {product.price.toLocaleString()}
                </p>
              </div>
              <div className="flex-1">
                <p className="font-body text-[10px] text-stone-400">銷售價</p>
                <p className="font-display text-sm font-bold text-emerald-600">
                  NT$ {product.sellingPrice.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="font-body text-[10px] text-stone-400">利潤</p>
                <span className="font-body text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                  NT$ {profit.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-body text-[10px] text-stone-500">
                  已售 {product.raised} / {product.stockQuantity} 件
                </span>
                <span className="font-body text-[10px] text-stone-500">
                  剩餘 {remaining} 件
                </span>
              </div>
              <div className="w-full h-2 rounded-full bg-stone-100 overflow-hidden">
                <div
                  className={`h-full rounded-full ${progressColor} transition-all duration-700`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </button>

        {/* Price & CTA */}
        <div className="px-4 md:px-5 pb-4 md:pb-5 pt-0 flex items-center justify-between gap-3">
          <div>
            <span className="font-display text-lg md:text-xl font-bold text-rose-800">
              NT$ {product.price.toLocaleString()}
            </span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOrder();
            }}
            className="font-body text-xs md:text-sm font-semibold px-4 py-2.5 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-colors whitespace-nowrap shrink-0"
          >
            {isLoggedIn ? "立即認購" : "登入下單"}
          </button>
        </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => onOrder(product.id)}
      />
    </>
  );
}

interface ProductGridProps {
  products: TrialProduct[];
  onOrder: (productId: string) => void;
}

export default function ProductGrid({ products, onOrder }: ProductGridProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
      {products.map((product) => (
        <ProductCard key={product.id} product={product} onOrder={onOrder} />
      ))}
    </div>
  );
}